import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core';
import { useImagesLoaded } from '../../hooks/useImagesLoaded';
import Sparkles from './Sparkles';
import { COLOR_BLACK, COLOR_WHITE, COLOR_STEAK_SAUCE } from '../colors';
import { addCommas } from '../../utils';
import { opacityMatrix } from './animation-config';
import queryString from 'query-string';

const ROOT_WIDTH = 900;
const ROOT_HEIGHT = 600;
const PIXEL_DENSITY_FACTOR = 2;
const KEYFRAME_COUNT = 10;
const getScaleFactor = (props)=> props && !isNaN(props.scaleFactor) ? props.scaleFactor : PIXEL_DENSITY_FACTOR;

const useStyles = makeStyles((theme)=> ({
    root: {
        width: (props)=> ROOT_WIDTH * getScaleFactor(props),
        height: (props)=> ROOT_HEIGHT * getScaleFactor(props),
        position: 'relative',
        fontFamily: ['"LL Circular Pro"', 'Arial', 'Helvetica', 'sans-serif'],
        overflow: 'hidden',
        backgroundColor: COLOR_WHITE,
        textRendering: 'optimizeLegibility',
        color: COLOR_BLACK,
        borderRadius: (props)=> props && props.borderRadius ? props.borderRadius : 0,
    },
    viewport: {
        display: 'block',
        position: 'relative',
        width: '100%',
        height: '100%',
        zoom: (props)=> getScaleFactor(props),
    },
    heroWrap: {
        position: 'relative',
        width: '100%',
        height: 400,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        color: COLOR_WHITE,
    },
    shadowGradient: {
        position: 'absolute',
        width: '100%',
        height: 120,
        bottom: 0,
        left: 0,
        background: 'linear-gradient(0deg, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0) 100%)',
    },
    listingStatusWrap: {
        position: 'absolute',
        width: 278,
        height: 104,
        bottom: 80,
        left: 0,
    },
    listingStatusContainer: {
        position: 'relative',
        height: '100%',
        width: 300,
        backgroundColor: COLOR_BLACK,

        '&::before': {
            position: 'absolute',
            right: -52,
            top: 0,
            content: "''",
            height: 104,
            width: 52,
            backgroundColor: COLOR_BLACK,
            borderTopRightRadius: 52,
            borderBottomRightRadius: 52,
        },
    },
    listingStatusLabel: {
        position: 'absolute',
        fontSize: 36,
        left: 24,
        top: 38,
        fontWeight: 800,
        lineHeight: '28px',
        whiteSpace: 'nowrap',
    },
    sparklesLeft: {
        position: 'absolute',
        left: 24,
        bottom: -22,
    },
    sparklesRight: {
        position: 'absolute',
        right: -64,
        top: -16,

    },
    cityName: {
        position: 'absolute',
        fontSize: 22,
        fontWeight: 500,
        lineHeight: '32px',
        left: 24,
        bottom: 24,
    },
    relativeDiv: {
        position: 'relative',
        width: '100%',
        height: '100%',
    },
    ctaText: {
        fontSize: '26px',
        lineHeight: '28px',
        fontWeight: 800,
    },
    disclaimerWrap: {
        position: 'absolute',
        width: '100%',
        bottom: 16,
        left: 24,
        color: COLOR_STEAK_SAUCE,
        fontSize: 10,
        lineHeight: '12px',
    },
    disclaimerLogo: {
        verticalAlign: '-1.5px',
    },
    disclaimer: {
        marginLeft: 16,
        display: 'inline-block',
    },
    bodyWrap: {
        display: 'flex',
        alignItems: 'center start',
        flexDirection: 'row',
        padding: 24,
        fontSize: '24px',
        lineHeight: '32px',
    },
    listingInfoWrap: {
        minWidth: 336,
    },
    price: {
        fontWeight: 800,
        fontSize: '36px',
        lineHeight: '48px',
    },
    listingInfo: {},
    agentInfo: {
        marginTop: 8
    }
}));

export default function RecentlySoldShareable(props) {
    const classes = useStyles(props);
    const location = useLocation();
    let parsedData = props.data || null;
    let currentFrame = props.frame || 1;
    const onImagesLoaded = props.onImagesLoaded;
    if(!parsedData) {
        try {
            const { data, frame } = queryString.parse(location.search);
            parsedData = JSON.parse(data);
            currentFrame = parseInt(frame, 10);
            if(currentFrame > KEYFRAME_COUNT) {
                currentFrame = currentFrame % KEYFRAME_COUNT;
            }
        } catch(e) {
            console.error(e);
        }
    }
    const {
        city,
        hero,
        photos,
        listing_status_label,
        price,
        bedrooms,
        bathrooms,
        sqft,
        agent,
    } = parsedData || {};
    const heroImage = (photos && photos.length) ? photos[0] : hero;
    const loadedBgs = useImagesLoaded([heroImage]);

    useEffect(() => {
        if(loadedBgs && !window.isLoaded) {
            if(typeof onImagesLoaded === 'function') {
                onImagesLoaded();
            } else {
                window.isLoaded = true;
            }
        }
    }, [loadedBgs, onImagesLoaded]);

    if(!parsedData) return null;

    return (
        <>
            <link rel="stylesheet" href="./css/ll-circular-pro.css" />
            <div className={classes.root} id="templateRoot">
                <div className={classes.viewport}>

                    {/* Hero Block */}
                    <div className={classes.heroWrap}
                        style={{
                            backgroundImage: `url("${heroImage}")`
                        }}
                    >
                        <div className={classes.shadowGradient}></div>
                        <div className={classes.listingStatusWrap}>
                            <div className={classes.listingStatusContainer}>
                                <div className={classes.listingStatusLabel}>{(listing_status_label || 'RECENTLY SOLD').toUpperCase()}</div>
                                <div className={classes.sparklesLeft}>
                                    <Sparkles opacityMatrix={opacityMatrix.left[currentFrame - 1]} />
                                </div>
                                <div className={classes.sparklesRight}>
                                    <Sparkles opacityMatrix={opacityMatrix.right[currentFrame - 1]} />
                                </div>
                            </div>
                        </div>
                        <div className={classes.cityName}>in {city}</div>
                    </div>


                    {/* Hero Block */}
                    <div className={classes.bodyWrap}>
                        <div className={classes.listingInfoWrap}>
                            <div>Sale price</div>
                            <div className={classes.price}>${addCommas(price)}</div>
                            <div className={classes.listingInfo}>
                                {bedrooms > 0 && (<span>{bedrooms} bd</span>)}{' '}
                                {bedrooms > 0 && (bathrooms > 0 || sqft > 0) && (<span>&bull;</span>)}{' '}

                                {bathrooms > 0 && (<span>{bathrooms} ba</span>)}{' '}
                                {bathrooms > 0 && (sqft > 0 && (<span>&bull;</span>))}{' '}

                                {sqft > 0 && (<span>{addCommas(sqft)} sqft</span>)}{' '}
                            </div>
                        </div>
                        <div className={classes.agentInfoWrap}>
                            <div style={{fontWeight: 800}}>Contact me to buy or sell your next home!</div>
                            {agent && (
                                <div className={classes.agentInfo}>
                                    <div style={{fontWeight: 600}}>{agent.name}</div>
                                    {agent.email && (<div>{agent.email}</div>)}
                                </div>
                            )}
                        </div>
                    </div>


                    {/* Disclaimer */}
                    <div className={classes.disclaimerWrap}>
                        <img className={classes.disclaimerLogo} src="./images/lr-logo-disclaimer-grey.svg" height={11} alt="LR Logo" />
                        <div className={classes.disclaimer}>
                            The information in this report is from third-party sources
                            and its accuracy cannot be guaranteed
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
